import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { ThumbsUp, ThumbsDown } from '@phosphor-icons/react';
import { fmtDatetime } from './helpers';

export const EVVApprovalDialog = ({
  open, onOpenChange, action, entryId, selectedIds, notes, setNotes,
  processing, onConfirm,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle style={{ fontFamily: 'Manrope' }}>
          {action === 'approve' ? 'Approve EVV Entries' : 'Reject EVV Entries'}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          {action === 'approve'
            ? `You are about to approve ${entryId ? '1 entry' : `${selectedIds.length} entries`}. Approved entries can be submitted to Sandata.`
            : `You are about to reject ${entryId ? '1 entry' : `${selectedIds.length} entries`}. Staff will need to correct and re-submit for approval.`}
        </p>
        <div className="space-y-1">
          <Label className="text-xs">{action === 'approve' ? 'Approval Notes (optional)' : 'Rejection Reason'}</Label>
          <Textarea
            placeholder={action === 'approve' ? 'Optional approval notes...' : 'Explain why these entries are being rejected...'}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            data-testid="approval-notes-input"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button
          onClick={onConfirm}
          disabled={processing || (action === 'reject' && !notes.trim())}
          className={action === 'approve' ? 'bg-teal-600 hover:bg-teal-700' : 'bg-rose-600 hover:bg-rose-700'}
          data-testid="confirm-approval-btn"
        >
          {processing
            ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            : action === 'approve' ? <ThumbsUp size={16} className="mr-2" /> : <ThumbsDown size={16} className="mr-2" />}
          {action === 'approve' ? 'Approve' : 'Reject'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const EVVSchedulerHistoryDialog = ({ open, onOpenChange, history }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle style={{ fontFamily: 'Manrope' }}>Auto-Submit History</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        {history.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">No auto-submit runs yet</p>
        ) : (
          history.map((run) => (
            <div key={run.id} className="bg-slate-50 rounded-lg p-3 border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">{fmtDatetime(run.run_at)}</span>
                <span className="text-xs text-slate-500">{run.total_entries} entries</span>
              </div>
              <div className="flex gap-2 text-xs">
                <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">{run.accepted} accepted</span>
                {run.failed > 0 && (
                  <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded">{run.failed} failed</span>
                )}
              </div>
              {run.failed_entries && run.failed_entries.length > 0 && (
                <div className="mt-2 space-y-1">
                  {run.failed_entries.slice(0, 3).map((fe, idx) => (
                    <div key={fe.entry_id || idx} className="text-xs text-red-600 bg-red-50 rounded px-2 py-1">
                      {fe.patient_name || fe.entry_id}: {fe.error_code} - {fe.error_message}
                    </div>
                  ))}
                  {run.failed_entries.length > 3 && (
                    <p className="text-xs text-slate-400">+{run.failed_entries.length - 3} more</p>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </DialogContent>
  </Dialog>
);
